import * as Sentry from '@sentry/browser'
import * as React from 'react'
import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { RouteComponentProps, Link } from 'react-router'
import {
  Button,
  ButtonGroup,
  Callout,
  Card,
  Drawer,
  Elevation,
  FormGroup,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  Tag,
  TextArea,
  Tooltip
} from '@blueprintjs/core'
import { connect } from 'react-redux'
import CX from 'classnames'
import { IconNames } from '@blueprintjs/icons'
import uniq from 'lodash.uniq'
import { GenericCheckbox, LoadableSelect } from '../../components/Inputs'
import { ExpressListCategorySelect } from './ExpressListCategorySelect'
import Client from '../../client'
import { showFailure, showToast } from '../../Toast'
import { getErrorString } from '../../utils/serverErrorUtils'
import './style.scss'
import { AppState } from '../../store'
import { CurrentState } from '../../current/types'
import { ExpressListPriceInput } from './ExpressListPriceInput'
import {
  applyDefaultBoxPadding,
  applyDefaultBoxPaddingIfDimensionsPresent,
  parseBoxPadding,
  renderErrorButPreferRequired
} from '../products_v5/components/JoinedForm/Helpers'
import { ListingStrategy, MarketSettings } from '../../types/api'
import { createEmptyExpressListProduct, ExpressListProductState, getPreferredStrategy, safeIsEmpty } from './utils'
import { SHOPGOODWILL_CHANNEL_ID, PreservedListingOptions } from './types'
import { sgwFormReducer } from './sgwFormReducer'
import { DescriptionOrAttributes } from './inputs'
import {
  getProductAttrsFromTemplateForm,
  mergePreviousTemplateFormValues,
  setDraftProductValuesInTemplateForm,
  setNotesValuesInTemplateForm
} from '../products_v5/utils'
import compiler from '../admin/templates/builder/Engine'
import { TemplateFormItem } from '../products_v5/components/JoinedForm/types'
import config from '../../config'
import Uploader from '../../components/images/Uploader'
import TagSelector from '../product_manager/components/TagSelector'
import NotesRenderer from '../../components/Notes'
import { StrategyPicker } from './StrategyPicker'
import { ProgressBar } from './ProgressBar'
import { Timer } from './Timer'
import { useTimerSeconds } from './useTimerSeconds'
import CHANNELS from '../../constants/Channels'
import { DimensionInput } from './__fixtures__/DimensionInput'
import { ShippingBoxSelect } from '../products_v5/components/JoinedForm/ProductForm/ShippingBoxSelect'
import { SvgIcon } from './SvgIcon'
import WeightInput from '../../printing/components/WeightInput'
import { tryPrintExpressListProductLabel } from './expressListPrint'
import { useScaleStreamer } from '../../printing/scale'
import { ScaleContext } from '../../printing/ScaleContext'
import { IndexedInventoryLocationSelect } from '../../components/inputs/IndexedInventoryLocationSelect'
import { stringToBool } from '../../utils/string'
import useRememberedState from '../../hooks/useRememberedState'

const AUTO_PRINT_STORAGE_KEY = 'autoPrintAfterExpressListSave'

interface Props extends RouteComponentProps<{ id: string }> {
  current: CurrentState
  property: string
}

type FetchedProduct = ExpressListProductState

const supplierSelectOptions = (data: { suppliers: { id: number; name: string; allowedForStrategy?: boolean }[] }) =>
  data.suppliers.map(s => ({
    value: s.id,
    label: s.allowedForStrategy === false ? `${s.name} (Not allowed for the selected strategy)` : s.name,
    disabled: s.allowedForStrategy === false
  }))

const initializeTemplateState = (
  fetchedProduct: FetchedProduct,
  templates: any[],
  previousTemplateForm?: TemplateFormItem[]
) => {
  const template = templates[0]

  // It's possible this step isn't necessary, if we go with "Possible approach A".
  // Or maybe this is effectively the initialization of the template form with draft field values
  // (versus the "Possible approach A" that updates template values on-the-fly so the engine
  // will include width/etc in the title/description), TBD.
  const templateWithDraftProductValues = setDraftProductValuesInTemplateForm(fetchedProduct, template)

  const templateWithAppliedValues = setNotesValuesInTemplateForm(fetchedProduct.notes, templateWithDraftProductValues)

  if (previousTemplateForm?.length) {
    return mergePreviousTemplateFormValues(previousTemplateForm, templateWithAppliedValues)
  }

  return templateWithAppliedValues
}

const FORM_VERSION_INTEGER = 7 // JoinedForm is products v5. There was a prototype products_v6 that Jackson built. So ExpressList is version 7.
const FORM_VERSION_STRING = 'v7'

const ExpressListForm = (props: Props) => {
  const { id } = props.match.params
  const isNewProduct = id === 'new'
  const existingProductId = isNewProduct ? null : parseInt(id, 10)

  // Passed from ExpressListStart via react-router state (search result or empty new product).
  const fetchedProduct =
    (props.history.location.state as FetchedProduct) || (isNewProduct ? createEmptyExpressListProduct() : undefined)
  const skuFromDraft = !!fetchedProduct?.sku

  const [loadingCategoryConfiguration, setLoadingCategoryConfiguration] = useState(false)
  const [loadingStrategyConfiguration, setLoadingStrategyConfiguration] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // analytics state
  const [didChangeDescription, setDidChangeDescription] = useState(false)
  const didChangeDescriptionRef = useRef(didChangeDescription)
  didChangeDescriptionRef.current = didChangeDescription
  const [didChangeBuyNowPrice, setDidChangeBuyNowPrice] = useState(false)
  const [didChangeShippingPrice, setDidChangeShippingPrice] = useState(false)
  const [didChangeShippingMethod, setDidChangeShippingMethod] = useState(false)

  // product fields
  const [title, setTitle] = useState(fetchedProduct?.title || '')
  const [canUseSeoTitle, setCanUseSeoTitle] = useState(false)
  const [description, setDescriptionNoAnalytics] = useState(fetchedProduct?.description || '')
  const setDescription = React.useCallback((newDescription: string) => {
    setDidChangeDescription(true)
    setDescriptionNoAnalytics(newDescription)
  }, [])

  const initialCategoryId = fetchedProduct?.category?.id || null
  const [categoryId, setCategoryId] = useState<number>(initialCategoryId)
  const categoryIdRef = useRef(categoryId)
  categoryIdRef.current = categoryId
  const categoryConfigRequestIdRef = useRef(0)
  const [images, setImages] = useState<any>(fetchedProduct?.images || [])
  const nonRemovedImages = React.useMemo(() => (images || []).filter(i => !i.removed), [images])
  const [tags, setTags] = useState<string[]>(fetchedProduct?.tags || [])
  const [additionalNote, setAdditionalNote] = useState('')
  const [sku, setSku] = useState(fetchedProduct?.sku || null)
  const [weight, setWeight] = useState(fetchedProduct?.weight > 0 ? fetchedProduct.weight.toString() : '')
  // We don't want to offer shipping weight, but some admins have template fields mapped to it.
  // So we need to let them change their template fields and do what they would expect.
  // This also enables orgs who set weight and shipping weight before posting time (if anyone does that).
  const [shippingWeight, setShippingWeight] = useState(
    parseFloat(fetchedProduct?.shippingWeight) > 0 ? fetchedProduct.shippingWeight : null
  )
  const [scaleName, scaleWeight, scaleConnected] = useScaleStreamer()
  const [length, setLength] = useState(fetchedProduct?.length > 0 ? fetchedProduct.length.toString() : '')
  const [width, setWidth] = useState(fetchedProduct?.width > 0 ? fetchedProduct.width.toString() : '')
  const [height, setHeight] = useState(fetchedProduct?.height > 0 ? fetchedProduct.height.toString() : '')
  const [shippingBoxId, setShippingBoxId] = useState<number>(fetchedProduct?.shippingBox?.id)
  const paddingChoices = props.current?.organization?.shippingSettings?.paddingChoices
  const initialBoxPadding = applyDefaultBoxPadding(
    { boxPadding: parseBoxPadding(fetchedProduct?.boxPadding) },
    paddingChoices
  ).boxPadding
  const [boxPadding, setBoxPadding] = useState<number | null>(initialBoxPadding)
  const [inventoryLocationId, setInventoryLocationId] = useState<number>(null) // not initialized b/c we omit this field if it's already set.
  const [supplierId, setSupplierId] = useState<number>(fetchedProduct?.supplier?.id ?? null)

  const [marketSettings, setMarketSettings] = useState<MarketSettings>(null)

  // fields defined by category configuration
  const [strategies, setStrategies] = useState<ListingStrategy[]>()

  // fields defined by strategy
  const [strategyId, setStrategyId] = useState<number>(null)
  // The channel id(s) on the 1st step.
  // These are the channels for which we allow the user to configure price.
  // const [firstStepChannelIds, setFirstStepChannelIds] = useState<unknown[]>(null) // LATER we'll want this

  // Has fields like price, bidIncrement, auctionStart, etc.
  // Includes fields that we show to the user.
  // Includes fields that we never show to the user, but they're easy to pull from the strategy configuration, to include in the request body when submitting.
  // This will still need a little massaging before we make an API call, for some duplicated fields like seller_private_description.
  //
  // A lot of these are typed as strings, though you would think it should be a number.
  // - Sometimes this helps with typing into numeric inputs
  // - Sometimes this stems from strategy configurations that give us the value as a string
  const [sgwForm, dispatch] = useReducer(sgwFormReducer, {})
  const calculatedShipping = !sgwForm.shippingPrice || parseFloat(sgwForm.shippingPrice) === 0.0 // if false, then flat-rate shipping

  const sgwPriceLabel = sgwForm.listingType === 'AUCTION' ? 'Starting bid' : 'Price'

  const [templateForm, setTemplateForm] = useState<TemplateFormItem[]>(null)
  const [templateLayout, setTemplateLayout] = useState<unknown[]>(null)
  const [templatePreviewTitle, setTemplatePreviewTitle] = useState<string>(null)
  const [templateBody, setTemplateBody] = useState<string>(null)
  const [templateAdminTitle, setTemplateAdminTitle] = useState(null)
  const [templateId, setTemplateId] = useState<number>(null)

  const recompileTemplateOutput = useCallback(
    (form: Record<string, unknown>[], templatePreviewTitle1: string, templateBody1: string) => {
      const newDescription = compiler(templateBody1, form, false, true)
      setDescriptionNoAnalytics(newDescription)
    },
    []
  )

  const syncMappedProductFieldsFromTemplateForm = useCallback((form: TemplateFormItem[]) => {
    const attrs = getProductAttrsFromTemplateForm(form)

    if (attrs.length !== undefined) setLength(attrs.length.toString())
    if (attrs.width !== undefined) setWidth(attrs.width.toString())
    if (attrs.height !== undefined) setHeight(attrs.height.toString())
    if (attrs.weight !== undefined) setWeight(attrs.weight.toString())
    if (attrs.shippingWeight !== undefined) setShippingWeight(attrs.shippingWeight.toString())
  }, [])

  const setSEOTitle = () => {
    setTitle(t => compiler(templatePreviewTitle || t, templateForm, true, true))
  }

  const onTemplateFormChange = useCallback(
    (index, value) => {
      setTemplateForm(t => {
        // Basic state management for the field contents
        const form = t.map((item: any, indexInner) => {
          if (indexInner === index) {
            if (item.productField === 'length') setLength(value)
            if (item.productField === 'width') setWidth(value)
            if (item.productField === 'height') setHeight(value)
            if (item.productField === 'weight') setWeight(value)
            if (item.productField === 'shippingWeight') setShippingWeight(value)

            return {
              ...item,
              props: {
                ...item.props,
                value
              }
            }
          }
          return item
        })

        // Every time the user changes builder template fields (product attributes), we want to recompile the template output.
        recompileTemplateOutput(form, templatePreviewTitle, templateBody)

        return form
      })
    },
    [recompileTemplateOutput, templatePreviewTitle, templateBody]
  )

  const onTemplateFormChangeCombobox = useCallback(
    (index, input: Record<string, unknown>) => {
      setTemplateForm(t => {
        const form = t.map((item: any, indexInner) => {
          if (indexInner === index) {
            return input
          }
          // Test this by creating a Builder Template with a combobox, then typing a custom value into it, then choosing another value.
          return item
        })

        // Every time the user changes builder template fields (product attributes), we want to recompile the template output.
        recompileTemplateOutput(form, templatePreviewTitle, templateBody)

        return form
      })
    },
    [recompileTemplateOutput, templatePreviewTitle, templateBody]
  )

  // the product fields which have a template field mapped to them. Like if "Couch width" is mapped to product.width
  let templateMappedFields = []
  templateForm?.forEach(item => {
    if (item.productField) {
      templateMappedFields.push(item.productField)
    }
  })
  templateMappedFields = uniq(templateMappedFields)

  const showDimensionsSection =
    !templateMappedFields.includes('length') ||
    !templateMappedFields.includes('width') ||
    !templateMappedFields.includes('height')

  // Prevents submission
  const missingRequiredFields = []
  let requiredFieldCount = 6
  if (safeIsEmpty(title)) missingRequiredFields.push('Title')
  if (safeIsEmpty(description)) missingRequiredFields.push('Description')
  if (safeIsEmpty(categoryId?.toString())) missingRequiredFields.push('Category')
  if (safeIsEmpty(strategyId?.toString())) missingRequiredFields.push('Listing Strategy')
  if (safeIsEmpty(sgwForm.price)) missingRequiredFields.push(sgwPriceLabel)
  if (safeIsEmpty(sgwForm.carrierId?.toString())) missingRequiredFields.push('Shipping Method') // SGW always requires this
  if (props.current?.organization?.productFormRequiredFields?.includes('images')) {
    // Other places have pretty nuanced logic around whether images are required, see Product.should_validate_images?
    // But since express list can't be used when there are listings, we can naively follow what they configure in the admin settings.
    // As long as they don't do something silly (like omit images from being required AND turn on "list immediately"), they won't face any failed listings.

    if (safeIsEmpty(nonRemovedImages)) missingRequiredFields.push('Images')
    requiredFieldCount += 1
  }
  const weightRequired =
    calculatedShipping ||
    props.current?.organization?.productFormRequiredFields?.includes('weight') ||
    (props.current?.organization?.productFormRequiredFields?.includes('shippingWeight') &&
      !templateMappedFields.includes('shippingWeight'))

  const shippingWeightRequired =
    // We usually don't even have a shipping weight field in express list.
    // But they might have a template field mapped to shipping weight.
    // This is debatably too nuanced/complicated, but let's try to honor various combinations of:
    // - template fields, mapped to Weight and/or Shipping Weight. Note, only fields mapped to weight (not shipping weight) will dedup the "Item Weight" product field.
    // - Required Posting Fields, either Weight and/or Shipping Weight
    // - calculated vs flat-rate shipping
    props.current?.organization?.productFormRequiredFields?.includes('shippingWeight') &&
    templateMappedFields.includes('shippingWeight')

  if (weightRequired && !templateMappedFields.includes('weight')) {
    if (weight === '') missingRequiredFields.push('Weight')

    requiredFieldCount += 1
  }
  const boxRequired = props.current?.organization?.productFormRequiredFields?.includes('shippingBoxId')
  if (boxRequired) {
    if (safeIsEmpty(shippingBoxId?.toString())) {
      missingRequiredFields.push('Shipping Box')
    }
    requiredFieldCount += 1
  }
  // We don't want people to assign locations at list time (versus using Link + scanning).
  // Yet a portion of customers do this and configure it as required in admin settings.
  const inventoryLocationRequired =
    props.current?.organization?.productFormRequiredFields?.includes('inventoryLocationId') &&
    !fetchedProduct?.inventoryLocation
  if (inventoryLocationRequired) {
    if (safeIsEmpty(inventoryLocationId?.toString())) {
      missingRequiredFields.push('Inventory Location')
    }

    requiredFieldCount += 1
  }
  const supplierRequired =
    props.current?.organization?.productFormRequiredFields?.includes('supplierId') && !fetchedProduct?.supplier
  if (supplierRequired) {
    if (safeIsEmpty(supplierId?.toString())) {
      missingRequiredFields.push('Supplier')
    }

    requiredFieldCount += 1
  }

  // Massage templateForm so that we mark fields as required, even if the builder template didn't configure it as required,
  // but our biz logic here DOES require it.
  // Namely, if they're using calculated shipping and thus weight is required, then template fields mapped to weight should be required.
  const massagedTemplateForm = templateForm?.map(item => {
    let overrideToRequired = false

    // Our biz logic currently never requires dimensions. But if we did, we would want this:
    // if (item.productField === 'length' || item.productField === 'width' || item.productField === 'height') {
    //   if (dimensionsRequired) overrideToRequired = true
    // }

    if (item.productField === 'weight') {
      if (weightRequired) {
        overrideToRequired = true
      }
    }

    if (item.productField === 'shippingWeight') {
      if (shippingWeightRequired) {
        overrideToRequired = true
      }
    }

    return {
      ...item,
      props: {
        ...item.props,
        required: overrideToRequired ? true : item.props.required,

        // if a weight-mapped template field becomes required due to our biz logic (calculated shipping), then force it to be shown
        hidden: overrideToRequired ? '' : item.props.hidden
      }
    }
  })

  let templateHasValue = false
  massagedTemplateForm?.forEach(item => {
    let empty
    const { value } = item.props
    const fieldHasValue = value !== undefined && value !== null && value !== '' && value !== false

    if (fieldHasValue) templateHasValue = true

    if (item.type === 'checkbox') {
      empty = !value // making a checkbox required doesn't make a lot of sense, but honor this if they configure it
    } else {
      empty = safeIsEmpty(value as string)
    }

    if (!item.props.required) {
      return
    }

    // Prevent hidden required fields from stopping the user from progressing.
    const hidden =
      item.props.hidden && // performance optimization, skip compiler and fromJS
      !safeIsEmpty(item.props.hidden) && // I'm not sure if item.props.hidden will be null or empty string.
      stringToBool(compiler(item.props.hidden, templateForm, false, false))
    if (hidden) {
      return
    }

    requiredFieldCount += 1
    if (empty) {
      missingRequiredFields.push(item.props.label)
    }
  })

  if (canUseSeoTitle !== templateHasValue) setCanUseSeoTitle(templateHasValue)

  const formLoading = loadingCategoryConfiguration || loadingStrategyConfiguration
  const formLoaded = !formLoading && sgwForm && Object.keys(sgwForm).length > 1

  // clear any fields that were configured by the strategy configuration
  const clearStrategyConfig = () => {
    setStrategyId(null)
    dispatch({ type: 'clearStrategyForm' })
  }

  const getPreservedListingOptions = (): PreservedListingOptions => ({
    noCombineShipping: sgwForm.noCombineShipping,
    itemFeatured: sgwForm.itemFeatured
  })

  // clear any fields that were configured by the category or strategy configuration
  const clearCategoryAndStrategyConfig = (preservedListingOptions?: PreservedListingOptions) => {
    setCategoryId(null)
    categoryIdRef.current = null
    setTemplateForm(null) // template was configured by the selected category, so clear it
    setTemplateLayout(null)
    setTemplateAdminTitle(null)
    setTemplateId(null)
    // setFirstStepChannelIds(null) // LATER

    // strategy was generally auto-selected by the selected category, so clear it
    setStrategyId(null)
    dispatch({ type: 'clearForm', preservedListingOptions })
  }

  // Redirect back to the starting page if they navigated straight to a draft product URL without state.
  // Functionally, we want users to use the kickoff page. This helps avoid listing a product twice.
  // Technically, it avoids having to fetch the product in 2 different ways.
  // New products use id "new" and can initialize from createEmptyExpressListProduct() when state is missing.
  useEffect(() => {
    if (!fetchedProduct) {
      props.history.replace('/express_list')
    }
  }, [fetchedProduct, props.history])

  // Get the configuration for this listing strategy, such as starting price for each channel
  const fetchStrategyConfiguration = useCallback(async strategyIdToFetch => {
    try {
      setLoadingStrategyConfiguration(true)
      const res = await Client.listingStrategyConfigurationLookup(strategyIdToFetch)
      const strategyConfiguration = res.data.configuration

      // LATER revisit where we pull this info once we support multiple channels (and thus concurrent steps)
      // setFirstStepChannelIds([strategyConfiguration[0].marketId])

      // The list of channels (ids) for which this product will have listings.
      const channelIds = strategyConfiguration.map(c => c.marketId)
      if (channelIds.length > 1 || strategyConfiguration[0].marketId !== SHOPGOODWILL_CHANNEL_ID) {
        // In theory, there is no way to reach this branch. We pass marketIds to Client.categoryListingConfiguration
        const strategyName: string = res.data.strategy.name
        showToast(
          `Strategy "${strategyName}" isn't supported just yet. Please test with a SGW-only strategy, thanks`,
          Intent.WARNING,
          false,
          5000
        )

        clearStrategyConfig()

        return
      }

      // Initialize channel form data based on the strategy configuration
      dispatch({ type: 'strategyConfigLoaded', resData: res.data })
    } catch (e) {
      Sentry.captureException(e)

      // Clears out price fields, possibly from another category's strategy, which might be invalid.
      // Also forces them to choose the strategy again, to fire off API call again
      clearStrategyConfig()

      // The API response (if this is an API failure) won't have a useful message
      showFailure('Something went wrong, please choose a strategy again', 8000)
    } finally {
      setLoadingStrategyConfiguration(false)
    }
  }, [])

  // Get the configuration for this category, such as the list of strategies that can be used.
  const loadCategoryConfiguration = useCallback(
    async (
      categoryIdToFetch,
      options?: { previousTemplateForm?: TemplateFormItem[]; preservedListingOptions?: PreservedListingOptions }
    ) => {
      categoryConfigRequestIdRef.current += 1
      const requestId = categoryConfigRequestIdRef.current
      const isLatestCategoryConfigRequest = () => requestId === categoryConfigRequestIdRef.current

      const params = {
        // The MVP only supports SGW.
        // See "This isn't supported just yet. Please test with a SGW-only strategy, thanks"
        marketIds: [CHANNELS.SHOPGOODWILL.id]
        // LATER handle ebay multi acct
      }

      try {
        setLoadingCategoryConfiguration(true)
        const res = await Client.categoryListingConfiguration(categoryIdToFetch, params)

        if (!isLatestCategoryConfigRequest()) {
          return
        }

        const { templates, listingStrategies } = res.data

        setStrategies(listingStrategies)
        setMarketSettings(res.data.marketSettings)

        // The ID (in our system) of a CategoryMarketCategory record.
        // Represents a category on SGW.
        const cmcId = res.data.category?.categoryMappings
          ?.filter(cm => cm.active)
          .find(cm => cm.categoryMarketCategory.marketId === SHOPGOODWILL_CHANNEL_ID)?.categoryMarketCategory?.id

        if (!cmcId) {
          // Classic listing handles this case - it would just require the poster to choose a channel category.
          // But express list shouldn't handle it, since we don't want the user to choose trivial things like that.
          // LATER For Ebay, we will allow the user to choose categories (supporting orgs who want their users to choose the final leaf category).
          const message = `Category "${res.data.category.pathCache}" isn't configured for Shopgoodwill. Please ask your admin to fix this.`

          // A stale auto-load of the draft category should not wipe a category the user picked afterward.
          if (categoryIdToFetch === categoryIdRef.current || categoryIdRef.current == null) {
            clearCategoryAndStrategyConfig()
          }
          showToast(message, Intent.WARNING, false, 8000)

          return
        }

        if (templates.length > 0) {
          const template = initializeTemplateState(fetchedProduct, templates, options?.previousTemplateForm)
          setTemplateForm(template.form)
          setTemplateLayout(template.layout)
          setTemplatePreviewTitle(template.previewTitle)
          setTemplateBody(template.body)
          setTemplateAdminTitle(template.title)
          setTemplateId(template.id)
          syncMappedProductFieldsFromTemplateForm(template.form)
          // Recompile unless the user manually edited the description (e.g. static template insertions).
          if (!didChangeDescriptionRef.current) {
            recompileTemplateOutput(template.form, template.previewTitle, template.body)
          }
        }

        if (!isLatestCategoryConfigRequest()) {
          return
        }

        if (categoryIdToFetch === categoryIdRef.current) {
          dispatch({
            type: 'categoryConfigLoaded',
            resData: res.data,
            cmcId,
            preservedListingOptions: options?.preservedListingOptions
          })

          if (listingStrategies.length > 0) {
            const newStrategyId = getPreferredStrategy(listingStrategies)?.id
            setStrategyId(newStrategyId)

            fetchStrategyConfiguration(newStrategyId)
          }
        }
      } catch (e) {
        Sentry.captureException(e)

        // A stale request should not wipe a category the user picked afterward.
        if (categoryIdToFetch === categoryIdRef.current || categoryIdRef.current == null) {
          // Clears out any fields, possibly from another category, which might be invalid.
          // Also forces them to choose the category again, to fire off API call again
          clearCategoryAndStrategyConfig()

          // The API response (if this is an API failure) won't have a useful message
          showFailure('Something went wrong, please choose a category again', 8000)
        }
      } finally {
        if (isLatestCategoryConfigRequest()) {
          setLoadingCategoryConfiguration(false)
        }
      }
    },
    [fetchStrategyConfiguration, fetchedProduct, recompileTemplateOutput, syncMappedProductFieldsFromTemplateForm]
  )

  const onChangeStrategy = async newStrategyId => {
    if (newStrategyId) {
      // Don't leave stale form values from a previously selected strategy.
      // Particularly important for fields like "shipping price", where null means something significant (calculated shipping).
      clearStrategyConfig()

      setStrategyId(newStrategyId)
      await fetchStrategyConfiguration(newStrategyId)
    } else {
      // they typed backspace to clear the value. We can't prevent this behavior.
      clearStrategyConfig()
    }
  }

  // Handle case where we display this page, and the category was already set on the product.
  // Note, react fast refresh code changes will fire useEffect calls more frequently than regular dev / production.
  useEffect(() => {
    if (initialCategoryId) {
      loadCategoryConfiguration(initialCategoryId)
    }
    // Intentionally omit loadCategoryConfiguration: only auto-load the draft category on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategoryId])

  // Automatically generate a sku. Wait for a required supplier so prefixes match and we don't burn a sequence.
  useEffect(() => {
    let cancelled = false

    if (!skuFromDraft && fetchedProduct && !(supplierRequired && !supplierId)) {
      const maybeGenerateSku = async () => {
        try {
          const newSku = (await Client.newSku(supplierId ?? fetchedProduct.supplier?.id)).data.sku
          if (!cancelled) setSku(newSku)
        } catch (e) {
          Sentry.captureException(e)
          if (!cancelled) showToast('Could not generate SKU, please try again', Intent.WARNING, false)
        }
      }

      maybeGenerateSku()
    }

    return () => {
      cancelled = true
    }
  }, [fetchedProduct, skuFromDraft, supplierId, supplierRequired])

  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [submitFieldErrors, setSubmitFieldErrors] = useState(null)
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null)
  const [shouldPrint, setShouldPrint] = useRememberedState(AUTO_PRINT_STORAGE_KEY, false, false)

  const elapsedSeconds = useTimerSeconds()
  const { listerConnectEnabled } = props.current.organization.settings

  const submit = async () => {
    setLoadingSubmit(true)
    try {
      // Serialize the product + listing attributes

      const { listingType, price, buyItNowPrice, carrierId, ...otherSgwAttributes } = sgwForm
      const shopgoodwillListingAttributes: Record<string, any> = { ...otherSgwAttributes }

      shopgoodwillListingAttributes.title = title
      shopgoodwillListingAttributes.description = description

      if (marketSettings?.shopgoodwill?.sendSkuAsPrivateDescription) {
        shopgoodwillListingAttributes.sellerPrivateDescription = sku
      }

      if (listingType === 'AUCTION') {
        shopgoodwillListingAttributes.startingBid = price

        if (buyItNowPrice) {
          shopgoodwillListingAttributes.buyNowPrice = buyItNowPrice
        }
      }
      if (listingType === 'FIXED_PRICE') {
        shopgoodwillListingAttributes.startingBid = price
        shopgoodwillListingAttributes.buyNowPrice = price
      }
      if (shopgoodwillListingAttributes.shippingPrice === null) {
        delete shopgoodwillListingAttributes.shippingPrice
      }

      let templateFieldValuesAsJson = null
      if (templateForm) {
        // For example, [
        //   { "name": "Brand", "value": "Patagonia", "type": "input" },
        //   { "name": "Size", "value": "XL", "type": "select" }
        // ]
        const templateFieldValues = templateForm.map(e => {
          let { value } = e.props
          if (e.type === 'checkbox') {
            value = !!value // make sure unchecked checkboxes are serialized as false
          }

          return {
            name: e.props.label,
            value,
            type: e.type
          }
        })

        // templateFieldValues has the crazy keys we want (with spaces, symbols, etc). But axios is snake-casing them.
        // Ideally we would disable that just for this one key, using the "preservedKeys" config option.
        // But preservedKeys isn't recursive, so just send up a JSON string.
        templateFieldValuesAsJson = JSON.stringify(templateFieldValues)
      }

      const imagesAttributes = images
        .filter(i => !i.loading)
        .sort((a, b) => (a.removed ? 1 : 0) - (b.removed ? 1 : 0)) // put removed images at the end, so remaining images don't skip ranks
        .map((i, rank) => {
          const { removed, ...endpointAttributes } = i // strip non-endpoint attributes (nice to have)

          return { ...endpointAttributes, rank, rotation: i.rotation % 360, customDestroyFlag: i.removed }
        })

      // This helper function from classic list generates several attributes, like LWH and tags.
      // We're only pulling tags here, because we want to manage the other state in more real-time,
      // so that the other state can be used for real-time validation (like drawing the progress bar).
      const tagsFromTemplate = templateForm && getProductAttrsFromTemplateForm(templateForm).tags
      let allTags = tags
      if (tagsFromTemplate) {
        allTags = allTags.concat(tagsFromTemplate)
      }

      const resolvedBoxPadding = applyDefaultBoxPaddingIfDimensionsPresent(
        { boxPadding, length, width, height },
        paddingChoices
      ).boxPadding

      const request: Record<string, unknown> = {
        ...(existingProductId != null ? { id } : {}),
        title,
        description,
        categoryId,
        listingStrategyId: strategyId,
        carrierId, // re: our domain model- carrier should be on the listing. It's stored on the product, then copied onto the listing.
        sku,
        imagesAttributes,
        tags: allTags,
        additionalNote,
        length,
        width,
        height,
        weight: weight === '' ? '0.0' : weight, // this jives with how serialization / defaults work on the backend
        // We don't need to allow users to enter an item weight PLUS a higher shipping weight
        // (for the case of having 2 lbs worth of packing peanuts + corrugate).
        // But, honor it if they already set it on the product. Or if they mapped a builder template field to shipping weight.
        shippingWeight: shippingWeight || weight,
        shippingBoxId,
        boxPadding: resolvedBoxPadding,
        formVersion: FORM_VERSION_INTEGER, // Used for GMV analytics.
        shopgoodwillListingAttributes,
        templateFieldValuesAsJson
      }
      if (inventoryLocationId) {
        // We only display this form field if it's required and missing.
        // For other cases, omit this field to use existing value. Also sidesteps bug where someone reshelfs while this form was being filled out.
        request.inventoryLocationId = inventoryLocationId
      }
      if (supplierId) {
        // We only display this form field if it's required and missing.
        // For other cases, omit this field to use existing value on the draft product.
        request.supplierId = supplierId
      }
      setSubmitFieldErrors(null)
      setSubmitErrorMessage(null)

      const res = await Client.createProductV5(request)

      const success = res.data.product.id && res.data.product.title
      if (success) {
        showToast(`${sku || 'Product'} was listed`, Intent.SUCCESS)

        const reactRouterState: Record<string, unknown> = {
          requiredFieldCount,
          elapsedSeconds
        }

        let autoPrinted = false
        if (shouldPrint && listerConnectEnabled) {
          autoPrinted = true
          const printResult = await tryPrintExpressListProductLabel({
            productId: res.data.product.id,
            sku: typeof sku === 'string' ? sku : null
          })
          if (!printResult.ok) {
            reactRouterState.printFailed = true
            reactRouterState.printProductId = res.data.product.id
            if (printResult.skuTooLong) {
              reactRouterState.printSkuTooLong = true
            }
            if (typeof sku === 'string') {
              reactRouterState.printSku = sku
            }
          }
        }

        // Replace, because clicking back and seeing the same form again would be bad.
        // The product was already saved, and bugs will happen if they submit a second time.
        props.history.replace('/express_list', reactRouterState)

        analytics.track('Product Created Express List', {
          version: FORM_VERSION_STRING,
          productId: res.data.product.id,
          isNewProduct,
          organizationId: props.current.organization.id,
          timeToListDuration: elapsedSeconds,
          autoPrinted,
          didChangeDescription, // It's ambitious, but maybe users won't have to change the description
          didChangeBuyNowPrice, // fields in the drawer. Hopefully users aren't changing these.
          didChangeShippingPrice,
          didChangeShippingMethod
        })
      }
    } catch (e) {
      if (e.response?.data?.errors && Object.keys(e.response.data.errors).length > 0) {
        setSubmitFieldErrors(e.response.data.errors)

        // Show errors in the callout, but only if they're not junk AND not displayed elsewhere

        // Unpack the fields that we:
        // - explicitly parse and show to the user below a form field
        // - would never show to the user (because it's noise / a duplicate / etc)
        const {
          shopgoodwillListingStartingBid, // we explicitly parse
          shopgoodwillListingDescription, // duplicate of product description
          shopgoodwillListing, // noise
          ...otherErrorFields
        } = e.response.data.errors

        if (Object.keys(otherErrorFields).length === 0) {
          // There's nothing that can only be shown in the callout
          setSubmitErrorMessage('Please fix the errors below and try again.')
        } else if (otherErrorFields.variantsPrice && otherErrorFields.variantsPrice[0] === 'is required') {
          // Users shouldn't be trying to list draft products with variants. But I did, and the default message is confusing.
          setSubmitErrorMessage('Products with variants are unsupported in Express List.')
        } else {
          // Some error fields couldn't be displayed in the form.
          // So to be safe, show a decent message to the user
          setSubmitErrorMessage(
            `Please fix these errors and try again: ${getErrorString(
              { response: { data: { errors: otherErrorFields } } },
              Sentry.captureException
            )}`
          )
        }
      } else {
        // In this situation, they're probably getting a 500, so getErrorString is probably "We're sorry, something went wrong"
        setSubmitErrorMessage(`Error: ${getErrorString(e, Sentry.captureException)}`)
      }
    } finally {
      setLoadingSubmit(false)
    }
  }

  const saveButtonEnabled = formLoaded && missingRequiredFields.length === 0
  const saveButtonText = shouldPrint ? 'Save, list & print' : 'Save & list'
  const saveButtonIcon = shouldPrint ? IconNames.PRINT : IconNames.DOCUMENT
  const saveTooltipContent = (
    <div className="tooltip-content" data-testid="save-button-tooltip">
      <span>Please fill out:</span>
      <ul>
        {missingRequiredFields.map(s => (
          <li key={s}>{s}</li>
        ))}
      </ul>
    </div>
  )

  const onChangeShippingBox = React.useCallback((e: { name: string; value: number }) => {
    setShippingBoxId(e.value)
  }, [])

  const onChangeImages = React.useCallback((newImages, cb) => {
    // newImages will only have the images we passed into Uploader.
    // We still need to keep all the images (including removed ones) in our state
    setImages(s => newImages.concat((s || []).filter(i => i.removed)))
    if (cb) {
      cb()
    }
  }, [])

  if (!fetchedProduct) {
    // Users shouldn't see this.
    // Avoids memory leaks from LoadableSelect running.
    return (
      <div>
        <p>Returning to starting page...</p>
      </div>
    )
  }

  const showTooltipsDuringTesting = config.TEST ? { isOpen: true } : {}

  return (
    <div className="express-list-page">
      <div className="header sticky">
        <div className="header-content">
          <div className="left-header-things">
            <Link to="/express_list">
              <Button text="Back" icon={IconNames.CHEVRON_LEFT} intent={Intent.PRIMARY} minimal />
            </Link>
            <h1>Express List</h1>

            <ProgressBar
              formLoading={formLoading}
              requiredFieldCount={requiredFieldCount}
              missingRequiredFields={missingRequiredFields}
              topRightElement={<Timer elapsedSeconds={elapsedSeconds} />}
            />
          </div>

          {listerConnectEnabled ? (
            <ButtonGroup className="express-list-save-button-group">
              <Tooltip
                disabled={saveButtonEnabled}
                content={saveTooltipContent}
                position="bottom"
                {...showTooltipsDuringTesting}
              >
                <Button
                  large
                  intent={Intent.PRIMARY}
                  icon={saveButtonIcon}
                  onClick={submit}
                  loading={loadingSubmit}
                  disabled={!saveButtonEnabled}
                >
                  {saveButtonText}
                </Button>
              </Tooltip>
              <Popover
                position="bottom-right"
                disabled={!saveButtonEnabled}
                interactionKind="click"
                content={
                  <Menu>
                    <MenuItem
                      icon={IconNames.DOCUMENT}
                      text="Save & list"
                      disabled={!shouldPrint}
                      onClick={() => setShouldPrint(false)}
                    />
                    <MenuItem
                      icon={IconNames.PRINT}
                      text="Save, list & print"
                      disabled={shouldPrint}
                      onClick={() => setShouldPrint(true)}
                    />
                  </Menu>
                }
              >
                <Button
                  large
                  intent={Intent.PRIMARY}
                  rightIcon={IconNames.CARET_DOWN}
                  disabled={!saveButtonEnabled}
                  loading={loadingSubmit}
                  data-testid="express-list-save-menu-toggle"
                />
              </Popover>
            </ButtonGroup>
          ) : (
            <Tooltip
              disabled={saveButtonEnabled}
              content={saveTooltipContent}
              position="bottom"
              {...showTooltipsDuringTesting}
            >
              <Button intent={Intent.PRIMARY} onClick={submit} loading={loadingSubmit} disabled={!saveButtonEnabled}>
                Save and List
              </Button>
            </Tooltip>
          )}
        </div>
        <div className="dummy-collapsed-drawer-spacer" />
      </div>

      <div className="content-and-collapsed-drawer-container">
        <div className="main-content">
          {submitErrorMessage && (
            <div className="callout-container">
              <div className="callout-content">
                <Callout intent={Intent.DANGER}>{submitErrorMessage}</Callout>
              </div>
            </div>
          )}

          <div className="column-container">
            <div className="column">
              <Card elevation={Elevation.ONE} data-testid="card-product">
                <h1>PRODUCT</h1>
                <FormGroup
                  label="Title"
                  labelFor="title-input"
                  labelInfo={safeIsEmpty(title) && <Icon icon="warning-sign" />}
                >
                  <div className="title-input-container">
                    {/* Doesn't show the max length yet, but counting chars for them eliminates 80% of the friction around title length */}
                    <Tag className="char-count-tag" minimal>
                      {title ? title.length : 0}
                    </Tag>

                    {/* Hide the button if there are no product attributes.
                    Show it before the category/template/etc is loaded to minimize flickering. People usually use builder templates. */}
                    {(!formLoaded || templateForm) && (
                      <Tag className="use-seo-title" minimal>
                        <Tooltip
                          content={
                            <div style={{ maxWidth: 275 }}>
                              {canUseSeoTitle
                                ? 'Auto-populate a Search Engine Optimized title from product attribute field values'
                                : 'Please fill out Product Attributes first'}
                            </div>
                          }
                        >
                          <Button
                            minimal
                            className={canUseSeoTitle ? 'use-seo-title-button' : 'use-seo-title-button-disabled'}
                            disabled={!canUseSeoTitle}
                            icon={<Icon iconSize={14} icon="clean" />}
                            onClick={setSEOTitle}
                          >
                            Generate SEO Title
                          </Button>
                        </Tooltip>
                      </Tag>
                    )}

                    <TextArea
                      id="title-input"
                      growVertically
                      value={title}
                      onChange={event => {
                        setTitle(event.target.value)
                      }}
                    />
                  </div>
                </FormGroup>

                <FormGroup label="Category" labelInfo={!categoryId && <Icon icon="warning-sign" />}>
                  <div data-testid="category-formgroup">
                    <ExpressListCategorySelect
                      load={() => {
                        // Hide categories that aren't mapped to SGW.
                        // This is good, b/c it prevents them from seeing this warning:
                        // > Category ... isn't configured for Shopgoodwill. Please ask your admin to fix this.
                        // This is bad, b/c it silently removes channels, and posters might not realize to ask their admin about mapping it.
                        return Client.categoryIndex([CHANNELS.SHOPGOODWILL.id])
                      }}
                      value={categoryId}
                      onChange={v => {
                        const newCategoryId = v
                        const previousTemplateForm = templateForm
                        const preservedListingOptions = getPreservedListingOptions()
                        clearCategoryAndStrategyConfig(preservedListingOptions)
                        setCategoryId(newCategoryId)
                        categoryIdRef.current = newCategoryId
                        if (newCategoryId) {
                          loadCategoryConfiguration(newCategoryId, { previousTemplateForm, preservedListingOptions })
                        }
                      }}
                      autoload
                    />
                  </div>
                </FormGroup>

                <StrategyPicker
                  maybe
                  label="Listing Strategy"
                  labelInfo={!strategyId && <Icon icon="warning-sign" />}
                  strategies={strategies}
                  strategyId={strategyId}
                  setStrategyId={onChangeStrategy}
                />

                {supplierRequired && (
                  <FormGroup
                    label="Supplier"
                    labelInfo={safeIsEmpty(supplierId?.toString()) && <Icon icon="warning-sign" />}
                  >
                    <div data-testid="supplier-form-group">
                      <LoadableSelect
                        load={() => Client.suppliersIndex({ listingStrategyId: strategyId })}
                        process={supplierSelectOptions}
                        onChange={setSupplierId}
                        value={supplierId}
                        autoload
                      />
                    </div>
                  </FormGroup>
                )}

                {inventoryLocationRequired && (
                  <FormGroup
                    label="Inventory Location"
                    labelInfo={safeIsEmpty(inventoryLocationId?.toString()) && <Icon icon="warning-sign" />}
                  >
                    <div data-testid="inventory-location-form-group">
                      <IndexedInventoryLocationSelect
                        autoload
                        value={inventoryLocationId}
                        onChange={setInventoryLocationId}
                        skipIndex
                      />
                    </div>
                  </FormGroup>
                )}
              </Card>

              {formLoading && (
                <Card elevation={Elevation.ONE} className="bp3-skeleton">
                  <div data-testid="configuration-loading-spinner">
                    {/* Placeholder makes the loading skeleton have the same height as final content. */}
                    <h1>loading placeholder</h1>
                    <FormGroup label="loading placeholder">
                      <ExpressListPriceInput value="0.00" onValueChange={_x => {}} />
                    </FormGroup>
                  </div>
                </Card>
              )}

              {formLoaded && (
                <div data-testid="card-sgw">
                  <Card elevation={Elevation.ONE} className={formLoading && 'bp3-skeleton'}>
                    <h1>SHOPGOODWILL</h1>

                    <FormGroup
                      label={sgwPriceLabel}
                      labelFor="sgw-price-input"
                      // Note, this red helperText should rarely appear. We disable submission if this is blank
                      // Test by triggering minimum_reserve_price (set reserve_price <= starting_bid)
                      helperText={renderErrorButPreferRequired(submitFieldErrors?.shopgoodwillListingStartingBid)}
                      labelInfo={safeIsEmpty(sgwForm.price) && <Icon icon="warning-sign" />}
                    >
                      <ExpressListPriceInput
                        // We considered making this icon flex (the icon inside the input), based on whether it was filled out or missing.
                        // However, for consistency with other fields, we're going to put the warning-sign on the text label.
                        leftIcon="dollar"
                        fill
                        // Note, this danger intent should rarely appear. We disable submission if this is blank
                        intent={submitFieldErrors?.shopgoodwillListingStartingBid ? Intent.DANGER : null}
                        id="sgw-price-input"
                        value={sgwForm.price}
                        onValueChange={(valueAsString: string) => {
                          dispatch({
                            type: 'onValueChange',
                            newValues: { price: valueAsString }
                          })
                        }}
                      />
                    </FormGroup>
                  </Card>
                </div>
              )}

              <div>
                <Card elevation={Elevation.ONE} data-testid="card-weight-measurements">
                  <h1>WEIGHT AND MEASUREMENTS</h1>
                  {/*
                  Note, weight and dimensions are always shown, even if they aren't required (like for flat-rate shipping).
                  It's moderately likely that buyers will want this info regardless.
                  It's conceivable that later steps of the strategy might change to calculated shipping.
                  */}

                  {!templateMappedFields.includes('weight') && (
                    <WeightInput
                      label="Item Weight"
                      labelFor="item-weight"
                      labelInfo={weightRequired && safeIsEmpty(weight) && <Icon icon="warning-sign" />}
                      value={weight}
                      onValueChange={(_valueAsNumber, valueAsString) => {
                        setWeight(valueAsString)
                      }}
                      min={0}
                      minorStepSize={0.001}
                      selectAllOnFocus
                      fill
                      buttonPosition="none"
                      scaleName={scaleName}
                      scaleWeight={scaleWeight}
                      scaleConnected={scaleConnected}
                    />
                  )}

                  {showDimensionsSection && (
                    <FormGroup label="Dimensions">
                      <div className="dimensions">
                        {!templateMappedFields.includes('length') && (
                          <DimensionInput
                            value={length}
                            onValueChange={(_valueAsNumber, valueAsString) => {
                              setLength(valueAsString)
                            }}
                            rightLabel="L"
                          />
                        )}

                        {!templateMappedFields.includes('width') && (
                          <DimensionInput
                            value={width}
                            onValueChange={(_valueAsNumber, valueAsString) => {
                              setWidth(valueAsString)
                            }}
                            rightLabel="W"
                          />
                        )}

                        {!templateMappedFields.includes('height') && (
                          <DimensionInput
                            value={height}
                            onValueChange={(_valueAsNumber, valueAsString) => {
                              setHeight(valueAsString)
                            }}
                            rightLabel="H"
                          />
                        )}
                      </div>
                    </FormGroup>
                  )}

                  <FormGroup label="Box Padding" className="nuke-top-margin-loadable-select">
                    <div data-testid="box-padding-formgroup">
                      <LoadableSelect
                        load={() => props.current.organization.shippingSettings.paddingChoices}
                        raw
                        process={shippingBoxPaddingChoices =>
                          shippingBoxPaddingChoices.map(choice => ({
                            value: parseFloat(choice.value),
                            label: choice.name
                          }))
                        }
                        onChange={setBoxPadding}
                        value={boxPadding}
                        autoload
                      />
                    </div>
                  </FormGroup>

                  {!calculatedShipping && (
                    <Callout
                      intent={Intent.PRIMARY}
                      className="flat-rate-shipping-callout"
                      icon={<SvgIcon path="/assets/icons/info-sign-inverted.svg" />}
                    >
                      <strong>Shipping Price Override</strong>
                      <br />
                      <p>
                        Calculated shipping not used, the shipping price is set to ${sgwForm.shippingPrice}. Use the
                        info panel to make changes.
                      </p>
                    </Callout>
                  )}
                </Card>
              </div>

              <Card elevation={Elevation.ONE}>
                <h1>SHIPPING DETAILS</h1>
                <div data-testid="shipping-box-formgroup">
                  <ShippingBoxSelect
                    onChange={onChangeShippingBox}
                    value={shippingBoxId}
                    length={length}
                    width={width}
                    height={height}
                    padding={boxPadding}
                    autoload
                    autoCalculate
                    labelInfo={boxRequired && safeIsEmpty(shippingBoxId?.toString()) && <Icon icon="warning-sign" />}
                  />
                </div>
              </Card>
            </div>
            <div className="column">
              <Card
                elevation={Elevation.ONE}
                className="card-descr-or-attributes"
                data-testid="card-descr-or-attributes"
              >
                <ScaleContext.Provider value={{ scaleWeight, scaleName, scaleConnected }}>
                  <DescriptionOrAttributes
                    description={description}
                    setDescription={setDescription}
                    templateForm={massagedTemplateForm}
                    templateLayout={templateLayout}
                    onTemplateFormChange={onTemplateFormChange}
                    onTemplateFormChangeCombobox={onTemplateFormChangeCombobox}
                    loaded={formLoaded}
                    categorySelected={!!categoryId}
                    strategySelected={!!strategyId}
                    templateId={templateId}
                  />
                </ScaleContext.Provider>
              </Card>
              {config.DEV && (
                // Somehow customers can do template maintenance without being told which template they saw. But I can't.
                <p>Template: {templateAdminTitle}</p>
              )}
            </div>

            <div className="column images-column">
              <Card elevation={Elevation.ONE} data-testid="images-card">
                <h1>IMAGES</h1>

                <div className="warning-icon-container bp3-text-muted">
                  {missingRequiredFields.includes('Images') && <Icon icon="warning-sign" />}
                </div>

                <Uploader maxImages={config.MAX_IMAGES} images={nonRemovedImages} onChange={onChangeImages} />

                <br />
                <br />
              </Card>

              <Card elevation={Elevation.ONE} data-testid="tags-card">
                <h1>TAGS</h1>

                <TagSelector value={tags} onChange={setTags} />
              </Card>

              <Card elevation={Elevation.ONE} className="notes-card" data-testid="notes-card">
                <h1>NOTES</h1>

                {existingProductId != null && (
                  <NotesRenderer allowNewOrEditNotes={false} resource="Product" resourceId={existingProductId} />
                )}

                <FormGroup label="Additional Note">
                  <TextArea
                    fill
                    placeholder="Enter any internal information about this product (e.g. handling instructions for warehouse). This will not be shown on public listings."
                    value={additionalNote}
                    onChange={e => setAdditionalNote(e.target.value)}
                  />
                </FormGroup>
              </Card>
            </div>

            <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(!isDrawerOpen)} size="500px">
              <div className={CX('bp3-dark', 'express-list-drawer')} data-testid="express-list-drawer">
                {formLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <h4>INVENTORY DETAILS</h4>

                    <Button
                      minimal
                      icon={<Icon iconSize={20} icon="export" />}
                      className="close-button"
                      data-testid="drawer-close-button"
                      onClick={() => setIsDrawerOpen(false)}
                    />

                    <FormGroup label="SKU:" inline className="sku-field">
                      {sku || <Spinner size={Spinner.SIZE_SMALL} />}
                    </FormGroup>

                    <hr />

                    <h4>LISTING STRATEGY</h4>

                    <div data-testid="drawer-strategy-formgroup">
                      <FormGroup label="Listing strategy:" inline>
                        <StrategyPicker
                          maybe={false}
                          label={null}
                          strategies={strategies}
                          strategyId={strategyId}
                          setStrategyId={onChangeStrategy}
                        />
                      </FormGroup>
                    </div>

                    <FormGroup label="Listing type:" inline>
                      {sgwForm.listingType === 'AUCTION' ? 'Auction' : 'Fixed'}
                    </FormGroup>

                    {sgwForm.buyItNowPrice && (
                      <FormGroup label="Buy Now Price:" labelFor="buy-now-price-input" inline>
                        <ExpressListPriceInput
                          id="buy-now-price-input"
                          value={sgwForm.buyItNowPrice}
                          nonClearable
                          onValueChange={(valueAsString: string) => {
                            dispatch({
                              type: 'onValueChange',
                              newValues: { buyItNowPrice: valueAsString }
                            })

                            setDidChangeBuyNowPrice(true)
                          }}
                        />
                      </FormGroup>
                    )}

                    <hr />

                    <h4>SHIPPING DETAILS</h4>

                    {sgwForm.shippingPrice && (
                      <FormGroup label="Shipping Price:" labelFor="shipping-price-input" inline>
                        <ExpressListPriceInput
                          id="shipping-price-input"
                          value={sgwForm.shippingPrice}
                          nonClearable
                          onValueChange={(valueAsString: string) => {
                            dispatch({
                              type: 'onValueChange',
                              newValues: { shippingPrice: valueAsString }
                            })

                            setDidChangeShippingPrice(true)
                          }}
                        />
                      </FormGroup>
                    )}

                    <FormGroup label="Shipping Method:" inline className="no-right-margin">
                      <LoadableSelect
                        className="maybe-dark-select"
                        load={Client.carriersIndex}
                        process={d => d.carriers.map(s => ({ value: s.id, label: s.name }))}
                        onChange={v => {
                          dispatch({
                            type: 'onValueChange',
                            newValues: { carrierId: v }
                          })

                          setDidChangeShippingMethod(true)
                        }}
                        value={sgwForm.carrierId}
                        style={{ marginTop: 0 }}
                        autoload
                        noLabel
                      />
                    </FormGroup>

                    <GenericCheckbox
                      title="No Combine Shipping"
                      value={sgwForm.noCombineShipping}
                      onChange={value => {
                        dispatch({
                          type: 'onValueChange',
                          newValues: { noCombineShipping: value }
                        })
                      }}
                    />
                    <GenericCheckbox
                      title="Featured Item (additional cost: $6.95)"
                      value={sgwForm.itemFeatured}
                      onChange={value => {
                        dispatch({
                          type: 'onValueChange',
                          newValues: { itemFeatured: value }
                        })
                      }}
                    />
                  </>
                )}
              </div>
            </Drawer>
          </div>
        </div>

        <div className="collapsed-drawer">
          {/* Remember to keep "dummy-collapsed-drawer-spacer" the same width as "collapsed-drawer" */}
          <Button
            className="drawer-open-button"
            data-testid="drawer-open-button"
            minimal
            icon={<Icon className="rotate-270" iconSize={20} icon="export" color="#CED9E0" />}
            onClick={() => setIsDrawerOpen(true)}
          />
        </div>
      </div>
    </div>
  )
}

export default connect((state: AppState) => ({ current: state.current }))(ExpressListForm)
